import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Separator } from '@/components/ui/separator';

interface InvoiceCreatorProps {
  po: any;
  onSuccess: () => void;
}

interface InvoiceLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export function InvoiceCreator({ po, onSuccess }: InvoiceCreatorProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  const [invoiceData, setInvoiceData] = useState({
    invoice_number: `INV-${Date.now()}`,
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
    notes: '',
  });

  // Better handling of bill_to information with fallbacks
  const billToInfo = {
    name: po.bill_to_name || po.vendor_name || 'Unknown District',
    address: po.bill_to_address && po.bill_to_address !== 'Address not provided' ? po.bill_to_address : '',
    city: po.bill_to_city && po.bill_to_city !== 'Unknown' ? po.bill_to_city : '',
    state: po.bill_to_state && po.bill_to_state !== 'Unknown' ? po.bill_to_state : '',
    zip: po.bill_to_zip && po.bill_to_zip !== '00000' ? po.bill_to_zip : '',
  };

  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>(
    po.po_line_items && po.po_line_items.length > 0 
      ? po.po_line_items.map((item: any) => ({
          description: item.description || `${item.item_number ? `Item ${item.item_number}: ` : ''}Professional Development Services`,
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          total_price: item.total_price || (item.quantity * item.unit_price) || 0,
        }))
      : [
          {
            description: 'Edugogy Professional Development Platform - Annual License',
            quantity: 1,
            unit_price: po.subtotal || po.total_amount || 0,
            total_price: po.subtotal || po.total_amount || 0,
          }
        ]
  );

  const handleInputChange = (field: string, value: string) => {
    setInvoiceData(prev => ({ ...prev, [field]: value }));
  };

  const handleLineItemChange = (index: number, field: keyof InvoiceLineItem, value: string | number) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Recalculate total for this item
      if (field === 'quantity' || field === 'unit_price') {
        const quantity = field === 'quantity' ? Number(value) : updated[index].quantity;
        const unitPrice = field === 'unit_price' ? Number(value) : updated[index].unit_price;
        updated[index].total_price = quantity * unitPrice;
      }
      
      return updated;
    });
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, {
      description: 'Professional Development Services',
      quantity: 1,
      unit_price: 0,
      total_price: 0,
    }]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(prev => prev.filter((_, i) => i !== index));
    }
  };

  const subtotal = lineItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
  const totalAmount = subtotal; // No tax for most educational institutions

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    
    try {
      // Create invoice record
      const { data: invoiceRecord, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          ...invoiceData,
          po_id: po.id,
          district_id: po.district_id,
          created_by: user.id,
          subtotal,
          total_amount: totalAmount,
        })
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // Create invoice line items
      const lineItemsToInsert = lineItems.map(item => ({
        invoice_id: invoiceRecord.id,
        ...item
      }));

      const { error: lineItemsError } = await supabase
        .from('invoice_line_items')
        .insert(lineItemsToInsert);

      if (lineItemsError) throw lineItemsError;

      // Update PO to mark invoice as created
      const { error: updateError } = await supabase
        .from('purchase_orders')
        .update({
          invoice_created: true,
          invoice_id: invoiceRecord.id
        })
        .eq('id', po.id);

      if (updateError) throw updateError;

      onSuccess();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({
        title: "Error",
        description: "Failed to create invoice",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Invoice Header */}
      <Card>
        <CardHeader>
          <CardTitle>Invoice Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="invoice_number">Invoice Number</Label>
              <Input
                id="invoice_number"
                value={invoiceData.invoice_number}
                onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="invoice_date">Invoice Date</Label>
              <Input
                id="invoice_date"
                type="date"
                value={invoiceData.invoice_date}
                onChange={(e) => handleInputChange('invoice_date', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="due_date">Due Date</Label>
              <Input
                id="due_date"
                type="date"
                value={invoiceData.due_date}
                onChange={(e) => handleInputChange('due_date', e.target.value)}
                required
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bill To Information */}
      <Card>
        <CardHeader>
          <CardTitle>Bill To</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm space-y-1">
            <p className="font-medium">{billToInfo.name}</p>
            {billToInfo.address && <p>{billToInfo.address}</p>}
            {(billToInfo.city || billToInfo.state || billToInfo.zip) && (
              <p>
                {[billToInfo.city, billToInfo.state, billToInfo.zip].filter(Boolean).join(', ')}
              </p>
            )}
            {po.vendor_name && po.vendor_name !== billToInfo.name && (
              <p className="text-muted-foreground">Vendor: {po.vendor_name}</p>
            )}
            {po.tax_exemption_number && (
              <p className="mt-2 text-muted-foreground">Tax Exempt: {po.tax_exemption_number}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Invoice Items</CardTitle>
            <Button variant="outline" size="sm" onClick={addLineItem}>
              Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {lineItems.map((item, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-start">
                <h4 className="font-medium">Item {index + 1}</h4>
                {lineItems.length > 1 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => removeLineItem(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    Remove
                  </Button>
                )}
              </div>
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <Label>Description</Label>
                  <Input
                    value={item.description}
                    onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                    placeholder="Service description"
                    required
                  />
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => handleLineItemChange(index, 'quantity', Number(e.target.value))}
                  />
                </div>
                <div>
                  <Label>Unit Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.unit_price}
                    onChange={(e) => handleLineItemChange(index, 'unit_price', Number(e.target.value))}
                  />
                </div>
                <div>
                  <Label>Total</Label>
                  <Input
                    value={`$${(item.total_price || 0).toFixed(2)}`}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
            </div>
          ))}
          
          <Separator />
          
          <div className="flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Tax:</span>
                <span>$0.00 (Tax Exempt)</span>
              </div>
              <div className="flex justify-between font-semibold text-lg border-t pt-2">
                <span>Total:</span>
                <span>${totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={invoiceData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Add any additional notes or payment instructions..."
            rows={3}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4">
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Creating Invoice...' : 'Create Invoice'}
        </Button>
      </div>
    </div>
  );
}